import { saveCallbackStatus } from '@finpay-development/shared-components';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { UNKNOWN_SERVER_ERROR } from '../../shared/state/saving-status';
import { LevelOfCare } from '../models/level-of-care';
import { PayerRow } from '../models/payers';
import {
  getBlankRule,
  getBlankRuleCriteriaParam,
  getDefaultRuleEdit,
  Rule,
  RuleCriteriaParam,
  RuleEditViewModel,
  RuleSet,
} from '../models/rule';
import { Scholarship } from '../models/scholarship';
import { States } from '../models/us-states';
import { ConfigRiskThreshold } from '../models/risk-threshold'
import {
  configGetLOC,
  configGetPayers,
  configGetScholarships,
  configGetRiskThresholds,
  configGetRiskClassSettings,
  configGetFinPayRiskClasses,
} from './admin-configuration-thunk';
import { ConfigRiskClassSetting, FinPayRiskClass } from '../models/risk-class-setting';

interface AdminContextState {
  rules: RuleSet;
  allRules: Rule[];
  currentRule: RuleEditViewModel;
  fullRule: Rule;
  currentRuleCriteriaParam: RuleCriteriaParam;
  allRuleCriteriaParams: RuleCriteriaParam[];
  usStates: States[],
  levelsOfCare: LevelOfCare[],
  payers: PayerRow[]
  payersMap: Map<number, PayerRow>
  modalSaveStatus: saveCallbackStatus;
  modalDeleteStatus: saveCallbackStatus;
  errorMessage: string;
  isSaveSuccessful: boolean;
  isLoading: {
    isLoadingRules: boolean;
    isLoadingPayer: boolean;
    isLoadingUsStates: boolean;
    isLoadingLevelsOfCare: boolean,
    getLevelsOfCareStatus: saveCallbackStatus,
    isLoadingScholarship: boolean,
    getScholarshipsStatus: saveCallbackStatus,
    isLoadingRiskThresholds: boolean,
    getRiskThresholdsStatus: saveCallbackStatus,
    isLoadingRiskClassSettings: boolean,
    getRiskClassSettingsStatus: saveCallbackStatus,
    isLoadingFinPayRiskClasses: boolean,
  },
  isError: {
    payer: boolean
  },
  scholarships: Scholarship[],
  riskThresholds: ConfigRiskThreshold[],
  riskClassSettings: ConfigRiskClassSetting[],
  finPayRiskClasses: FinPayRiskClass[],
}

const initialState = {
  rules: [],
  allRules: [],
  currentRule: getDefaultRuleEdit(),
  fullRule: getBlankRule(),
  currentRuleCriteriaParam: getBlankRuleCriteriaParam(),
  allRuleCriteriaParams: [],
  usStates: [],
  levelsOfCare: [],
  payers: [],
  payersMap:  new Map(),
  modalSaveStatus: saveCallbackStatus.none,
  modalDeleteStatus: saveCallbackStatus.none,
  errorMessage: "",
  isSaveSuccessful: true,
  isLoading: {
    isLoadingRules: false,
    isLoadingPayer: true,
    isLoadingUsStates: false,
    isLoadingLevelsOfCare: false,
    getLevelsOfCareStatus: saveCallbackStatus.none,
    isLoadingScholarship: false,
    getScholarshipsStatus: saveCallbackStatus.none,
    isLoadingRiskThresholds: false,
    getRiskThresholdsStatus: saveCallbackStatus.none,
    isLoadingRiskClassSettings: false,
    getRiskClassSettingsStatus: saveCallbackStatus.none,
    isLoadingFinPayRiskClasses: false,
  },
  isError: {
    payer: false
  },
  scholarships: [],
  riskThresholds: [],
  riskClassSettings: [],
  finPayRiskClasses: [],

} as AdminContextState;

const adminConfigurationSlice = createSlice({
  name: "adminConfigurationContext",
  initialState,
  reducers: {
    resetFormulaForm: (state) => {
      state.currentRuleCriteriaParam = getBlankRuleCriteriaParam();
    },
    clearStatus: (state) => {
      state.modalSaveStatus = saveCallbackStatus.none;
      state.modalDeleteStatus = saveCallbackStatus.none;
      state.errorMessage = "";
      state.isLoading.getLevelsOfCareStatus = saveCallbackStatus.none;
      state.isSaveSuccessful = true;
      state.isLoading.getScholarshipsStatus = saveCallbackStatus.none;
      state.isLoading.getRiskThresholdsStatus = saveCallbackStatus.none;
    },
    clearRiskClassSettingsStatus: (state) => {
      state.isLoading.getRiskClassSettingsStatus = saveCallbackStatus.none;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      // may not be needed. (unused currently). re-evaluate later
      state.isLoading.isLoadingRules = action.payload;
    },
    resetAdminConfigurationContext() {
      return initialState;
    },
    setClientScholarshipConfig: (state, action: PayloadAction<Scholarship[]>) => {
      state.scholarships = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(
      configGetPayers.fulfilled,
      (state, action: PayloadAction<any>) => {

        const allPayersMap = action.payload.reduce((payersMap: Map<number, PayerRow>, currPayer: PayerRow)=>{
          payersMap.set(currPayer.payorId, currPayer)
          return payersMap
        }, new Map())

        state.payers = action.payload;
        state.payersMap = allPayersMap;
        state.isLoading.isLoadingPayer = false;
        state.isError.payer = false
      }
    );
    builder.addCase(configGetPayers.pending, (state, action) => {
      state.isLoading.isLoadingPayer = true;
      state.isError.payer = false
    });
    builder.addCase(configGetPayers.rejected, (state, action) => {
      state.isLoading.isLoadingPayer = false;
      state.isError.payer = true
    });

    builder.addCase(
      configGetScholarships.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.scholarships = action.payload;
        state.isLoading.isLoadingScholarship = false;
        state.isLoading.getScholarshipsStatus = saveCallbackStatus.success;
      }
    );
    builder.addCase(configGetScholarships.pending, (state, action) => {
      state.isLoading.isLoadingScholarship = true;
      state.isLoading.getScholarshipsStatus = saveCallbackStatus.none;
    });
    builder.addCase(configGetScholarships.rejected, (state, action) => {
      state.isLoading.isLoadingScholarship = false;
      state.isLoading.getScholarshipsStatus = saveCallbackStatus.error;
    });

    builder.addCase(configGetLOC.pending, (state, action) => {
      state.isLoading.getLevelsOfCareStatus = saveCallbackStatus.none;
      state.isLoading.isLoadingLevelsOfCare = true;
    });

    builder.addCase(configGetLOC.fulfilled, (state, action: PayloadAction<any>) => {
      state.levelsOfCare = action.payload;
      state.isLoading.getLevelsOfCareStatus = saveCallbackStatus.success;
	    state.isLoading.isLoadingLevelsOfCare = false;
    });

    builder.addCase(configGetLOC.rejected, (state, action) => {
      state.isLoading.getLevelsOfCareStatus = saveCallbackStatus.error;
      state.isLoading.isLoadingLevelsOfCare = false;
    });

    builder.addCase(
      configGetRiskThresholds.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.riskThresholds = action.payload;
        state.isLoading.isLoadingRiskThresholds = false;
        state.isLoading.getRiskThresholdsStatus = saveCallbackStatus.success;
      }
    );

    builder.addCase(configGetRiskThresholds.pending, (state, action) => {
      state.isLoading.isLoadingRiskThresholds = true;
      state.isLoading.getRiskThresholdsStatus = saveCallbackStatus.none;
    });

    builder.addCase(configGetRiskThresholds.rejected, (state, action) => {
      state.isLoading.isLoadingRiskThresholds = false;
      state.isLoading.getRiskThresholdsStatus = saveCallbackStatus.error;
    });

    builder.addCase(
      configGetRiskClassSettings.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.riskClassSettings = action.payload;
        state.isLoading.isLoadingRiskClassSettings = false;
        state.isLoading.getRiskClassSettingsStatus = saveCallbackStatus.success;
      }
    );
    builder.addCase(configGetRiskClassSettings.pending, (state, action) => {
      state.isLoading.isLoadingRiskClassSettings = true;
      state.isLoading.getRiskClassSettingsStatus = saveCallbackStatus.none;
    });
    builder.addCase(configGetRiskClassSettings.rejected, (state, action) => {
      state.isLoading.isLoadingRiskClassSettings = false;
      state.isLoading.getRiskClassSettingsStatus = saveCallbackStatus.error;
    });

    builder.addCase(
      configGetFinPayRiskClasses.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.finPayRiskClasses = action.payload;
        state.isLoading.isLoadingFinPayRiskClasses = false;
      }
    );
    builder.addCase(configGetFinPayRiskClasses.pending, (state, action) => {
      state.isLoading.isLoadingFinPayRiskClasses = true;
    });
    builder.addCase(configGetFinPayRiskClasses.rejected, (state, action) => {
      state.isLoading.isLoadingFinPayRiskClasses = false;
    });
  },
});

export const {
  resetFormulaForm,
  clearStatus,
  clearRiskClassSettingsStatus,
  resetAdminConfigurationContext,
  setClientScholarshipConfig
} = adminConfigurationSlice.actions;

export default adminConfigurationSlice.reducer;
